import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config/config';
import { useAuth } from '../context/AuthContext';
import './VigilAIQuery.css';

/* ── DSL syntax highlighting (same tokeniser as VigilRuleBrowser) ────────── */
function highlightDSL(code) {
  if (!code) return null;
  const combined = new RegExp(
    '(//.*$)' +                                            // comments
    '|("(?:[^"\\\\]|\\\\.)*"|\'(?:[^\'\\\\]|\\\\.)*\')' + // strings
    '|(@[a-zA-Z_][\\w.]*)' +                               // @concepts
    '|(\\b(?:from|to|where|as)\\b)' +                      // flow keywords
    '|(\\b(?:find|flow|match|taint|source|sink|sanitizer|not|and|or|in)\\b)', // main keywords
    'gm'
  );
  const parts = [];
  let last = 0;
  let m;
  while ((m = combined.exec(code)) !== null) {
    if (m.index > last) parts.push(<span key={last}>{code.slice(last, m.index)}</span>);
    if (m[1])      parts.push(<span key={m.index} className="vaq-dsl-comment">{m[0]}</span>);
    else if (m[2]) parts.push(<span key={m.index} className="vaq-dsl-string">{m[0]}</span>);
    else if (m[3]) parts.push(<span key={m.index} className="vaq-dsl-concept">{m[0]}</span>);
    else if (m[4]) parts.push(<span key={m.index} className="vaq-dsl-flow">{m[0]}</span>);
    else if (m[5]) parts.push(<span key={m.index} className="vaq-dsl-keyword">{m[0]}</span>);
    else           parts.push(<span key={m.index}>{m[0]}</span>);
    last = m.index + m[0].length;
  }
  if (last < code.length) parts.push(<span key={last}>{code.slice(last)}</span>);
  return parts;
}

/* ── localStorage helpers ────────────────────────────────────────────────── */
const HISTORY_KEY = 'vaq_history';
const MAX_HISTORY = 10;

function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
  catch { return []; }
}

function saveHistory(list) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, MAX_HISTORY)));
}

/* ── example prompts ─────────────────────────────────────────────────────── */
const EXAMPLES = [
  'Find SQL injection in Go',
  'Detect hardcoded secrets',
  'Find XSS vulnerabilities in React',
  'Check for weak cryptography',
  'Find path traversal attacks',
  'Detect insecure deserialization in Java',
];

/* ── DSL quick reference lines ───────────────────────────────────────────── */
const DSL_REF_LINES = [
  { cmd: 'find pattern "..."',          desc: 'match code pattern' },
  { cmd: 'find call to any("...")',     desc: 'find function calls' },
  { cmd: 'find usage of any("...")',    desc: 'find usage patterns' },
  { cmd: 'find literal of any("...")',  desc: 'find literal values' },
  { cmd: 'find flow from @src to @snk', desc: 'taint flow analysis' },
];

/* ── category badge colour map ───────────────────────────────────────────── */
const CATEGORY_COLORS = {
  source:    '#f59e0b',
  sink:      '#ef4444',
  sanitizer: '#22c55e',
  guard:     '#3b82f6',
  crypto:    '#8b5cf6',
  auth:      '#6366f1',
  pii:       '#ec4899',
  credential:'#f97316',
};

function categoryColor(cat) {
  if (!cat) return '#64748b';
  return CATEGORY_COLORS[cat.toLowerCase()] || '#64748b';
}

/* ── severity colour helper ──────────────────────────────────────────────── */
function severityColor(sev) {
  switch ((sev || '').toLowerCase()) {
    case 'critical': return '#dc2626';
    case 'high':     return '#ef4444';
    case 'medium':   return '#f59e0b';
    case 'low':      return '#22c55e';
    default:         return '#64748b';
  }
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  VigilAIQuery                                                              */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function VigilAIQuery() {
  const { t } = useTranslation();
  const { token } = useAuth();

  /* ── mode: 'ai' | 'manual' ────────────────────────────────────────────── */
  const [mode, setMode] = useState('ai');

  /* ── shared state ──────────────────────────────────────────────────────── */
  const [targetPath, setTargetPath] = useState('');
  const [runLoading, setRunLoading] = useState(false);
  const [runResult, setRunResult] = useState(null);  // { findings, total, query, stderr }
  const [runError, setRunError] = useState('');

  /* ── AI translate state ────────────────────────────────────────────────── */
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);   // { query, explanation, provider, model }
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState(loadHistory);

  /* ── manual query state ────────────────────────────────────────────────── */
  const [manualQuery, setManualQuery] = useState('');
  const [dslRefOpen, setDslRefOpen] = useState(false);

  /* ── concept autocomplete state ────────────────────────────────────────── */
  const [concepts, setConcepts] = useState(null);     // cached concept list
  const [acVisible, setAcVisible] = useState(false);   // autocomplete dropdown open
  const [acFilter, setAcFilter] = useState('');         // text after @
  const [acIndex, setAcIndex] = useState(0);            // keyboard selection
  const [acStart, setAcStart] = useState(-1);           // cursor pos of @ char
  const manualRef = useRef(null);
  const acRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  /* ── persist history changes ───────────────────────────────────────────── */
  useEffect(() => { saveHistory(history); }, [history]);

  /* ── fetch concepts on mount ───────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    axios.get(`${config.BASE_URL}/vigil/concepts`, { headers })
      .then(res => { if (!cancelled) setConcepts(res.data?.concepts || []); })
      .catch(() => { /* ignore — autocomplete just won't work */ });
    return () => { cancelled = true; };
  }, [headers]);

  /* ── close autocomplete on click outside ───────────────────────────────── */
  useEffect(() => {
    function handleClick(e) {
      if (acRef.current && !acRef.current.contains(e.target) &&
          manualRef.current && !manualRef.current.contains(e.target)) {
        setAcVisible(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /* ── filtered concept list ─────────────────────────────────────────────── */
  const filteredConcepts = useMemo(() => {
    if (!concepts) return [];
    if (!acFilter) return concepts;
    const lower = acFilter.toLowerCase();
    return concepts.filter(c =>
      c.id?.toLowerCase().includes(lower) ||
      c.name?.toLowerCase().includes(lower)
    );
  }, [concepts, acFilter]);

  /* ── translate ─────────────────────────────────────────────────────────── */
  const translate = useCallback(async () => {
    const q = question.trim();
    if (!q) return;
    setLoading(true);
    setError('');
    setResult(null);
    setCopied(false);
    try {
      const res = await axios.post(
        `${config.BASE_URL}/vigil/ai/query`,
        { question: q },
        { headers }
      );
      setResult(res.data);
      setHistory(prev => {
        const filtered = prev.filter(h => h !== q);
        return [q, ...filtered].slice(0, MAX_HISTORY);
      });
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      if (err.response?.status === 503 || /not configured/i.test(msg)) {
        setError('no_ai');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [question, headers]);

  /* ── run query ─────────────────────────────────────────────────────────── */
  const runQuery = useCallback(async (dsl) => {
    const q = (dsl || '').trim();
    if (!q || !targetPath.trim()) return;
    setRunLoading(true);
    setRunResult(null);
    setRunError('');
    try {
      const res = await axios.post(
        `${config.BASE_URL}/vigil/query/run`,
        { query: q, target: targetPath.trim() },
        { headers }
      );
      setRunResult(res.data);
    } catch (err) {
      setRunError(err.response?.data?.error || err.response?.data?.stderr || err.message);
    } finally {
      setRunLoading(false);
    }
  }, [targetPath, headers]);

  /* ── copy to clipboard ─────────────────────────────────────────────────── */
  const copyQuery = useCallback(() => {
    if (!result?.query) return;
    navigator.clipboard.writeText(result.query).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [result]);

  /* ── click example chip ────────────────────────────────────────────────── */
  const pickExample = useCallback((ex) => { setQuestion(ex); }, []);

  /* ── click history item ────────────────────────────────────────────────── */
  const pickHistory = useCallback((q) => { setQuestion(q); }, []);

  /* ── handle key (AI mode) ──────────────────────────────────────────────── */
  const onKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) translate();
  }, [translate]);

  /* ── insert concept at cursor ──────────────────────────────────────────── */
  const insertConcept = useCallback((concept) => {
    const ta = manualRef.current;
    if (!ta || acStart < 0) return;
    const before = manualQuery.slice(0, acStart);
    const after  = manualQuery.slice(ta.selectionStart);
    const insert = `@${concept.id}`;
    const newVal = before + insert + after;
    setManualQuery(newVal);
    setAcVisible(false);
    // restore focus & cursor
    setTimeout(() => {
      ta.focus();
      const pos = before.length + insert.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  }, [manualQuery, acStart]);

  /* ── manual textarea onChange: detect @ trigger ────────────────────────── */
  const onManualChange = useCallback((e) => {
    const val = e.target.value;
    setManualQuery(val);
    const pos = e.target.selectionStart;
    // scan backwards from cursor to find @
    let i = pos - 1;
    while (i >= 0 && /[\w.]/.test(val[i])) i--;
    if (i >= 0 && val[i] === '@') {
      setAcStart(i);
      setAcFilter(val.slice(i + 1, pos));
      setAcVisible(true);
      setAcIndex(0);
    } else {
      setAcVisible(false);
    }
  }, []);

  /* ── manual textarea onKeyDown: autocomplete nav ───────────────────────── */
  const onManualKeyDown = useCallback((e) => {
    if (acVisible) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setAcIndex(i => Math.min(i + 1, filteredConcepts.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setAcIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (filteredConcepts[acIndex]) {
          e.preventDefault();
          insertConcept(filteredConcepts[acIndex]);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setAcVisible(false);
        return;
      }
    }
    // Ctrl/Cmd+Enter to run
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      runQuery(manualQuery);
    }
  }, [acVisible, acIndex, filteredConcepts, insertConcept, runQuery, manualQuery]);

  /* ── resolve current DSL for run button ────────────────────────────────── */
  const currentDSL = mode === 'manual' ? manualQuery : result?.query;
  const canRun = !!(currentDSL?.trim() && targetPath.trim());

  /* ── shorten file path for display ─────────────────────────────────────── */
  const shortPath = useCallback((p) => {
    if (!p) return '';
    const parts = p.split('/');
    return parts.length > 3 ? '…/' + parts.slice(-3).join('/') : p;
  }, []);

  /* ════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="vaq">
      {/* header */}
      <div className="vaq-header">
        <span className="vaq-header-icon">✨</span>
        <div className="vaq-header-text">
          <h2>{t('vigilAI.title')}</h2>
          <p>{t('vigilAI.subtitle')}</p>
        </div>
      </div>

      {/* mode tabs */}
      <div className="vaq-mode-tabs">
        <button
          className={`vaq-mode-tab${mode === 'ai' ? ' active' : ''}`}
          onClick={() => setMode('ai')}
        >
          ✨ {t('vigilAI.modeAI')}
        </button>
        <button
          className={`vaq-mode-tab${mode === 'manual' ? ' active' : ''}`}
          onClick={() => setMode('manual')}
        >
          ⚡ {t('vigilAI.modeManual')}
        </button>
      </div>

      {/* target repository selector */}
      <div className="vaq-target-bar">
        <label className="vaq-target-label">{t('vigilAI.targetPath')}</label>
        <input
          className="vaq-target-input"
          type="text"
          placeholder={t('vigilAI.targetPlaceholder')}
          value={targetPath}
          onChange={e => setTargetPath(e.target.value)}
        />
      </div>

      {/* split layout */}
      <div className="vaq-split">
        {/* ── left panel ─────────────────────────────────────────────────── */}
        <div className="vaq-card">
          {mode === 'ai' ? (
            <>
              {/* AI translate: textarea + examples */}
              <textarea
                className="vaq-textarea"
                placeholder={t('vigilAI.placeholder')}
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={onKeyDown}
              />

              <div className="vaq-examples-label">{t('vigilAI.examples')}</div>
              <div className="vaq-chips">
                {EXAMPLES.map(ex => (
                  <button key={ex} className="vaq-chip" onClick={() => pickExample(ex)}>
                    {ex}
                  </button>
                ))}
              </div>

              <div className="vaq-actions">
                <button
                  className="vaq-btn-translate"
                  onClick={translate}
                  disabled={!question.trim() || loading}
                >
                  {loading ? (
                    <>
                      {t('vigilAI.translating')}
                      <span className="vaq-dots"><span /><span /><span /></span>
                    </>
                  ) : t('vigilAI.translate')}
                </button>

                {result?.provider && (
                  <span className="vaq-provider-badge">
                    {result.provider}{result.model ? ` · ${result.model}` : ''}
                  </span>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Manual query: DSL editor + autocomplete */}
              <div className="vaq-manual-wrap">
                <textarea
                  ref={manualRef}
                  className="vaq-manual-editor"
                  placeholder="find flow from @user_input to @sql.execute"
                  value={manualQuery}
                  onChange={onManualChange}
                  onKeyDown={onManualKeyDown}
                  spellCheck={false}
                />
                {acVisible && filteredConcepts.length > 0 && (
                  <div className="vaq-autocomplete" ref={acRef}>
                    {filteredConcepts.slice(0, 15).map((c, i) => (
                      <button
                        key={c.id}
                        className={`vaq-autocomplete-item${i === acIndex ? ' active' : ''}`}
                        onMouseDown={(e) => { e.preventDefault(); insertConcept(c); }}
                      >
                        <span className="vaq-ac-name">@{c.id}</span>
                        <span
                          className="vaq-ac-badge"
                          style={{ background: categoryColor(c.category) }}
                        >
                          {c.category}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <p className="vaq-concept-hint">{t('vigilAI.conceptHint')}</p>

              {/* DSL Quick Reference (collapsible) */}
              <div className="vaq-dsl-ref">
                <button
                  className="vaq-dsl-ref-toggle"
                  onClick={() => setDslRefOpen(o => !o)}
                >
                  {dslRefOpen ? '▾' : '▸'} {t('vigilAI.dslRef')}
                </button>
                {dslRefOpen && (
                  <div className="vaq-dsl-ref-body">
                    {DSL_REF_LINES.map(l => (
                      <div key={l.cmd} className="vaq-dsl-ref-row">
                        <code>{l.cmd}</code>
                        <span>— {l.desc}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── right: results ─────────────────────────────────────────────── */}
        <div className="vaq-card">
          {/* error states (AI mode) */}
          {mode === 'ai' && error === 'no_ai' && (
            <div className="vaq-error">
              <strong>{t('vigilAI.noAI')}</strong><br />
              {t('vigilAI.noAIDesc')}{' '}
              <Link to="/ai/settings">AI Settings →</Link>
            </div>
          )}
          {mode === 'ai' && error && error !== 'no_ai' && (
            <div className="vaq-error">{error}</div>
          )}

          {/* AI translate result */}
          {mode === 'ai' && result ? (
            <>
              <div className="vaq-section-title">{t('vigilAI.generatedQuery')}</div>
              <pre className="vaq-dsl-block">{highlightDSL(result.query)}</pre>

              <div className="vaq-section-title">{t('vigilAI.explanation')}</div>
              <div className="vaq-explanation">{result.explanation}</div>

              <div className="vaq-result-btns">
                <button
                  className={`vaq-btn-copy${copied ? ' copied' : ''}`}
                  onClick={copyQuery}
                >
                  {copied ? '✓' : '📋'} {copied ? t('vigilAI.copied') : t('vigilAI.copyQuery')}
                </button>
                <button
                  className="vaq-btn-run"
                  disabled={!canRun || runLoading}
                  title={!targetPath.trim() ? t('vigilAI.targetPlaceholder') : ''}
                  onClick={() => runQuery(result.query)}
                >
                  {runLoading ? (
                    <>{t('vigilAI.running')} <span className="vaq-dots"><span /><span /><span /></span></>
                  ) : (
                    <>▶ {t('vigilAI.runQuery')}</>
                  )}
                </button>
              </div>
            </>
          ) : mode === 'ai' && !error && !loading && (
            <div className="vaq-empty">
              <div className="vaq-empty-icon">🧠</div>
              <div className="vaq-empty-text">
                {t('vigilAI.subtitle')}
              </div>
            </div>
          )}

          {mode === 'ai' && loading && !result && (
            <div className="vaq-empty">
              <div className="vaq-empty-icon">✨</div>
              <div className="vaq-empty-text">
                {t('vigilAI.translating')}
                <span className="vaq-dots" style={{ marginLeft: 6 }}>
                  <span /><span /><span />
                </span>
              </div>
            </div>
          )}

          {/* Manual mode: preview + run */}
          {mode === 'manual' && (
            <>
              {manualQuery.trim() ? (
                <>
                  <div className="vaq-section-title">{t('vigilAI.generatedQuery')}</div>
                  <pre className="vaq-dsl-block">{highlightDSL(manualQuery)}</pre>
                </>
              ) : (
                <div className="vaq-empty">
                  <div className="vaq-empty-icon">⚡</div>
                  <div className="vaq-empty-text">{t('vigilAI.conceptHint')}</div>
                </div>
              )}

              <div className="vaq-result-btns">
                <button
                  className="vaq-btn-run"
                  disabled={!canRun || runLoading}
                  title={!targetPath.trim() ? t('vigilAI.targetPlaceholder') : ''}
                  onClick={() => runQuery(manualQuery)}
                >
                  {runLoading ? (
                    <>{t('vigilAI.running')} <span className="vaq-dots"><span /><span /><span /></span></>
                  ) : (
                    <>▶ {t('vigilAI.runQuery')}</>
                  )}
                </button>
              </div>
            </>
          )}

          {/* history (both modes) */}
          {history.length > 0 && (
            <>
              <div className="vaq-history-title">
                <span>🕒</span> {t('vigilAI.history')}
              </div>
              <ul className="vaq-history-list">
                {history.map((q, i) => (
                  <li key={i}>
                    <button className="vaq-history-item" onClick={() => pickHistory(q)}>
                      <span className="vaq-history-icon">↩</span>
                      <span className="vaq-history-text">{q}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* ── query results section ──────────────────────────────────────────── */}
      {runLoading && (
        <div className="vaq-card" style={{ marginTop: '1.5rem' }}>
          <div className="vaq-empty">
            <div className="vaq-empty-icon">⏳</div>
            <div className="vaq-empty-text">
              {t('vigilAI.running')}
              <span className="vaq-dots" style={{ marginLeft: 6 }}>
                <span /><span /><span />
              </span>
            </div>
          </div>
        </div>
      )}

      {runError && (
        <div className="vaq-results" style={{ marginTop: '1.5rem' }}>
          <div className="vaq-results-error">
            <strong>{t('vigilAI.queryError')}</strong>
            <pre>{runError}</pre>
          </div>
        </div>
      )}

      {runResult && !runLoading && (
        <div className="vaq-results" style={{ marginTop: '1.5rem' }}>
          <div className="vaq-results-header">
            <span className="vaq-results-badge">{runResult.total ?? runResult.findings?.length ?? 0} {t('vigilAI.findings')}</span>
            {runResult.query && <code className="vaq-results-query">{runResult.query}</code>}
          </div>

          {runResult.stderr && (
            <div className="vaq-results-error">
              <pre>{runResult.stderr}</pre>
            </div>
          )}

          {(runResult.findings?.length > 0) ? (
            <div className="vaq-findings-table">
              {runResult.findings.map((f, i) => (
                <div
                  key={i}
                  className="vaq-findings-row"
                  style={{ borderLeftColor: severityColor(f.severity) }}
                >
                  <span className="vaq-finding-sev" style={{ color: severityColor(f.severity) }}>
                    {(f.severity || 'info').toUpperCase()}
                  </span>
                  <span className="vaq-finding-rule">{f.rule_id || f.ruleId || '—'}</span>
                  <span className="vaq-finding-file">{shortPath(f.file || f.location?.file)}</span>
                  <span className="vaq-finding-line">L{f.line || f.location?.line || '?'}</span>
                  <span className="vaq-finding-msg">{f.message || f.description || ''}</span>
                </div>
              ))}
            </div>
          ) : !runResult.stderr && (
            <div className="vaq-results-empty">
              <span className="vaq-results-empty-icon">✓</span>
              <span>{t('vigilAI.noFindings')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
